import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Organizer-only "Add Round" — a direct port of the old Matches tab's own
 * modal (ScheduleTab.jsx), same two fields (name + type) and same
 * round_robin/knockout values (still what the League/Playoff sections below
 * filter on). `onSave({ name, type })` is `useScheduleBuilder`'s `addRound`,
 * which already resolves the correct next `round_number` from the schedule's
 * real backend round numbers (not just a count) and refreshes live state —
 * this modal only owns its own form/loading/error state.
 *
 * Portaled to document.body, same reasoning as MatchSettingsModal.jsx: the
 * button that opens this (CrossGroupView.jsx) lives deep inside
 * GeneratedScheduleStep's `tf-animate-in` wrapper, whose animation touches
 * `transform` and leaves a non-`none` computed value even after finishing —
 * that traps `position: fixed` descendants inside the page's own scrolled
 * content instead of the true viewport, breaking centering. `stopPropagation`
 * on the outer div is separate and still needed even though nothing here
 * currently sits inside a clickable row — cheap insurance against the same
 * bubbling bug if that ever changes.
 */
const AddRoundModal = ({ isOpen, onClose, onSave }) => {
  const { theme } = useTheme();
  const [name, setName] = useState('');
  const [type, setType] = useState('round_robin');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const close = () => {
    setName('');
    setType('round_robin');
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await onSave({ name, type });
      close();
    } catch (e) {
      setError(e.message || 'Failed to create round');
      setIsSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />
      <div className={`relative w-full max-w-md rounded-2xl border ${theme.border} ${theme.card} ${theme.shadow} p-6`}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className={`text-lg font-bold ${theme.text}`}>Add New Round</h3>
          <button
            type="button"
            onClick={close}
            className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${theme.buttonGhost}`}
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className={`mb-2 block text-sm font-medium ${theme.text}`}>Round Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Quarter Finals, Group Stage"
              className={`w-full rounded-lg border px-3 py-2 text-sm ${theme.input}`}
            />
          </div>
          <div>
            <label className={`mb-2 block text-sm font-medium ${theme.text}`}>Round Type *</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className={`w-full rounded-lg border px-3 py-2 text-sm ${theme.input}`}
            >
              <option value="round_robin">League</option>
              <option value="knockout">Playoff / Knockout</option>
            </select>
          </div>
          {error && <p className="text-xs font-medium text-red-500">{error}</p>}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={close}
            disabled={isSaving}
            className={`rounded-lg border px-4 py-2 text-sm font-medium ${theme.border} ${theme.text} ${theme.buttonGhost} disabled:opacity-50`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!name.trim() || !type || isSaving}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${theme.buttonPrimary} hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {isSaving ? 'Creating…' : 'Create Round'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddRoundModal;
